import {
  SHRED_AWARD_META,
  SHRED_BASELINE_GAP_LABEL,
  SHRED_BASELINE_WINDOW_LABEL,
  SHRED_BRACKETS,
  SHRED_COLORS,
  SHRED_COPY,
  SHRED_PRIZE_IDS,
  SHRED_TV_PRIZE_DROP_FIRST_MIN_MS,
  SHRED_TV_PRIZE_DROP_GAP_MIN_MS,
  SHRED_TV_PRIZE_DROP_GAP_SPREAD_MS,
  SHRED_GHOST_RANGE_PCT,
  SHRED_ACTIVE_PLAY_MINUTES,
  SHRED_FULL_SESSION_MINUTES,
  SHRED_MAX_SESSION_MINUTES,
  SHRED_MAX_SESSIONS_PER_DAY,
  SHRED_ROOKIE_BASELINE_SESSIONS,
  SHRED_AWARD_ORDER,
  SHRED_BASELINE_DAYS,
  SHRED_BOARD_FILTERS,
  SHRED_CREW_MILESTONES,
  SHRED_DAY_COUNT,
  SHRED_FALLBACK_AVATAR_BASE,
  SHRED_FEED_TYPES,
  SHRED_MIN_BASELINE_SESSIONS,
  SHRED_MIN_SESSIONS,
  SHRED_POLL_MS,
  SHRED_STATUS,
  SHRED_VALUE_UNITS,
} from './constants';

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const toNumber = (value, fallback = 0) => {
  const parsed = typeof value === 'string' && value.trim() === '' ? NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPositiveRank = (value) => {
  const parsed = toNullableNumber(value);
  return parsed !== null && parsed >= 1 ? Math.floor(parsed) : null;
};

const toText = (value) => (typeof value === 'string' ? value : '');

/* ---------- status, countdown ---------- */

export const deriveStatus = (startMs, endMs, nowMs = Date.now()) => {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return SHRED_STATUS.UPCOMING;
  }
  if (nowMs < startMs) {
    return SHRED_STATUS.UPCOMING;
  }
  if (nowMs >= endMs) {
    return SHRED_STATUS.ENDED;
  }
  return SHRED_STATUS.LIVE;
};

// The server status is authoritative between polls, but the clock lets the hero flip the
// moment the countdown hits zero instead of waiting up to 60 s for the next response.
export const getEffectiveStatus = (challenge, nowMs = Date.now()) => {
  if (!challenge) {
    return SHRED_STATUS.UPCOMING;
  }
  const clockStatus = deriveStatus(challenge.startMs, challenge.endMs, nowMs);
  if (!Number.isFinite(challenge.startMs) || !Number.isFinite(challenge.endMs)) {
    return challenge.status;
  }
  const order = [SHRED_STATUS.UPCOMING, SHRED_STATUS.LIVE, SHRED_STATUS.ENDED];
  return order.indexOf(clockStatus) > order.indexOf(challenge.status) ? clockStatus : challenge.status;
};

// Server time minus local time at the moment the response arrived. Added to Date.now() it gives a
// server-corrected clock, so a fast or slow device clock cannot flip status or stop polling early.
export const getClockSkewMs = (generatedAtMs, receivedAtMs) =>
  Number.isFinite(generatedAtMs) && Number.isFinite(receivedAtMs) ? generatedAtMs - receivedAtMs : 0;

const STATUS_ORDER = [SHRED_STATUS.UPCOMING, SHRED_STATUS.LIVE, SHRED_STATUS.ENDED];
export const REFRESH_CATCH_UP_MS = 5000;

// Local timestamp at which the server-reported status should flip, or null when it never will.
export const getNextBoundaryMs = (challenge, skewMs = 0) => {
  if (!challenge) return null;
  if (challenge.status === SHRED_STATUS.UPCOMING && Number.isFinite(challenge.startMs)) {
    return challenge.startMs - skewMs;
  }
  if (challenge.status === SHRED_STATUS.LIVE && Number.isFinite(challenge.endMs)) {
    return challenge.endMs - skewMs;
  }
  return null;
};

// Poll interval for a fetched view: null = nothing fetched yet (caller decides), 0 = stop.
export const getRefreshIntervalMs = (view, nowMs = Date.now()) => {
  if (!view || !view.challenge) return null;
  const serverStatus = view.challenge.status;
  const clockStatus = getEffectiveStatus(view.challenge, nowMs + (view.clockSkewMs || 0));
  if (STATUS_ORDER.indexOf(clockStatus) > STATUS_ORDER.indexOf(serverStatus)) return REFRESH_CATCH_UP_MS;
  return serverStatus === SHRED_STATUS.ENDED ? 0 : SHRED_POLL_MS;
};

export const getCountdownTarget = (challenge, nowMs = Date.now()) => {
  const status = getEffectiveStatus(challenge, nowMs);
  if (status === SHRED_STATUS.UPCOMING) {
    return Number.isFinite(challenge?.startMs) ? challenge.startMs : null;
  }
  if (status === SHRED_STATUS.LIVE) {
    return Number.isFinite(challenge?.endMs) ? challenge.endMs : null;
  }
  return null;
};

export const formatCountdown = (targetMs, nowMs = Date.now()) => {
  if (!Number.isFinite(targetMs)) {
    return null;
  }
  const diff = Math.max(0, targetMs - nowMs);
  return {
    days: Math.floor(diff / MS_PER_DAY),
    hours: Math.floor((diff % MS_PER_DAY) / MS_PER_HOUR),
    minutes: Math.floor((diff % MS_PER_HOUR) / MS_PER_MINUTE),
    seconds: Math.floor((diff % MS_PER_MINUTE) / MS_PER_SECOND),
    totalMs: diff,
  };
};

export const getCurrentDay = (challenge, nowMs = Date.now()) => {
  const status = getEffectiveStatus(challenge, nowMs);
  if (status === SHRED_STATUS.UPCOMING) {
    return 0;
  }
  if (status === SHRED_STATUS.ENDED) {
    return challenge.dayCount;
  }
  if (!Number.isFinite(challenge.startMs)) {
    return Math.min(Math.max(challenge.currentDay, 1), challenge.dayCount);
  }
  const day = Math.floor((nowMs - challenge.startMs) / MS_PER_DAY) + 1;
  return Math.min(Math.max(day, 1), challenge.dayCount);
};

export const formatDateRange = (startMs, endMs, timeZone) => {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return '';
  }
  const build = (tz) => {
    const options = tz ? { month: 'short', day: 'numeric', timeZone: tz } : { month: 'short', day: 'numeric' };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    // endMs is the first instant after the last day, so step back to show the final day.
    return `${formatter.format(new Date(startMs))} – ${formatter.format(new Date(endMs - 1))}`;
  };
  try {
    return build(timeZone);
  } catch (e) {
    return build();
  }
};

/* ---------- number formatting ---------- */

export const formatNumber = (value) => {
  const parsed = toNullableNumber(value);
  return parsed === null ? '–' : Math.round(parsed).toLocaleString('en-US');
};

export const formatCompact = (value) => {
  const parsed = toNullableNumber(value);
  if (parsed === null) {
    return '–';
  }
  const abs = Math.abs(parsed);
  if (abs >= 1e9) return `${(parsed / 1e9).toFixed(2).replace(/\.?0+$/, '')}B`;
  if (abs >= 1e6) return `${(parsed / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (abs >= 1e4) return `${(parsed / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return Math.round(parsed).toLocaleString('en-US');
};

export const formatPct = (value, digits = 1) => {
  const parsed = toNullableNumber(value);
  if (parsed === null) {
    return '–';
  }
  const rounded = Number(parsed.toFixed(digits));
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toFixed(digits)}%`;
};

export const formatAwardValue = (unit, value) => {
  const parsed = toNullableNumber(value);
  if (parsed === null) {
    return '–';
  }
  switch (unit) {
    case SHRED_VALUE_UNITS.PCT:
      return formatPct(parsed);
    case SHRED_VALUE_UNITS.REPS:
      return `${formatNumber(parsed)} reps`;
    case SHRED_VALUE_UNITS.SESSIONS:
      return `${formatNumber(parsed)} ${Math.round(parsed) === 1 ? 'session' : 'sessions'}`;
    case SHRED_VALUE_UNITS.INDEX:
    default:
      return formatNumber(parsed);
  }
};

export const pluralize = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

/* ---------- normalisation ---------- */

export const normalizeEntry = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const id = raw.id === undefined || raw.id === null ? '' : String(raw.id);
  const username = toText(raw.username);
  if (!id && !username) {
    return null;
  }
  const rank = toPositiveRank(raw.rank);
  return {
    id: id || username,
    username: username || id,
    level: Math.max(0, Math.round(toNumber(raw.level))),
    bracket: raw.bracket === SHRED_BRACKETS.ROOKIE ? SHRED_BRACKETS.ROOKIE : SHRED_BRACKETS.REGULAR,
    rank,
    previousRank: toPositiveRank(raw.previousRank),
    sessions: Math.max(0, Math.round(toNumber(raw.sessions))),
    avgWork: toNumber(raw.avgWork),
    baselineAvgWork: toNumber(raw.baselineAvgWork),
    baselineSessions: Math.max(0, Math.round(toNumber(raw.baselineSessions))),
    improvementPct: toNumber(raw.improvementPct),
    qualified: raw.qualified === true && rank !== null,
    ineligibleLabel: toText(raw.ineligibleLabel).slice(0, 24),
    pace: toNumber(raw.pace),
    efficiency: toNullableNumber(raw.efficiency),
  };
};

export const normalizeChallenge = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {};
  const rewards = source.rewards && typeof source.rewards === 'object' ? source.rewards : {};
  const dayCount = Math.max(1, Math.round(toNumber(source.dayCount, SHRED_DAY_COUNT)));
  const validStatuses = Object.values(SHRED_STATUS);
  return {
    id: toText(source.id),
    name: toText(source.name),
    status: validStatuses.includes(source.status) ? source.status : SHRED_STATUS.UPCOMING,
    startMs: toNullableNumber(source.startMs),
    endMs: toNullableNumber(source.endMs),
    timezone: toText(source.timezone),
    dayCount,
    currentDay: Math.min(Math.max(Math.round(toNumber(source.currentDay)), 0), dayCount),
    minSessions: Math.max(1, Math.round(toNumber(source.minSessions, SHRED_MIN_SESSIONS))),
    baselineDays: Math.max(1, Math.round(toNumber(source.baselineDays, SHRED_BASELINE_DAYS))),
    minBaselineSessions: Math.max(
      1,
      Math.round(toNumber(source.minBaselineSessions, SHRED_MIN_BASELINE_SESSIONS))
    ),
    rookieBaselineSessions: Math.max(
      1,
      Math.round(toNumber(source.rookieBaselineSessions, SHRED_ROOKIE_BASELINE_SESSIONS))
    ),
    rewards: {
      participation: toText(rewards.participation),
      crewGoal: toText(rewards.crewGoal),
      podium: toText(rewards.podium),
    },
  };
};

const normalizeWinner = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const entry = normalizeEntry({ ...raw, rank: raw.rank });
  if (!entry) {
    return null;
  }
  return {
    rank: toPositiveRank(raw.rank) || 1,
    id: entry.id,
    username: entry.username,
    level: entry.level,
    value: toNumber(raw.value),
  };
};

export const normalizeAwards = (rawAwards) => {
  const list = Array.isArray(rawAwards) ? rawAwards : [];
  const byId = {};
  list.forEach((award) => {
    if (award && typeof award === 'object' && SHRED_AWARD_META[award.id]) {
      byId[award.id] = award;
    }
  });
  // Always return all five so the grid never reshuffles or disappears when data is thin.
  return SHRED_AWARD_ORDER.map((id) => {
    const meta = SHRED_AWARD_META[id];
    const award = byId[id] || {};
    const reward = award.reward && typeof award.reward === 'object' ? award.reward : {};
    const winners = (Array.isArray(award.winners) ? award.winners : [])
      .map(normalizeWinner)
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 3);
    return {
      id,
      icon: meta.icon,
      accent: meta.accent,
      title: toText(award.title) || meta.title,
      description: toText(award.description) || meta.description,
      valueUnit: Object.values(SHRED_VALUE_UNITS).includes(award.valueUnit)
        ? award.valueUnit
        : meta.valueUnit,
      winners,
      reward: {
        title: toText(reward.title),
        items: (Array.isArray(reward.items) ? reward.items : []).filter((item) => typeof item === 'string'),
      },
    };
  });
};

export const normalizeCrew = (rawCrew) => {
  const source = rawCrew && typeof rawCrew === 'object' ? rawCrew : {};
  const byDay = (Array.isArray(source.byDay) ? source.byDay : [])
    .filter((item) => item && typeof item === 'object')
    .map((item) => ({
      day: Math.round(toNumber(item.day)),
      dayKey: toText(item.dayKey),
      totalWork: toNumber(item.totalWork),
      sessions: Math.round(toNumber(item.sessions)),
    }))
    .filter((item) => item.day >= 1)
    .sort((a, b) => a.day - b.day);
  return {
    totalWork: toNumber(source.totalWork),
    goalWork: toNumber(source.goalWork),
    sessions: Math.round(toNumber(source.sessions)),
    participants: Math.round(toNumber(source.participants)),
    byDay,
  };
};

export const normalizeFeed = (rawFeed) =>
  (Array.isArray(rawFeed) ? rawFeed : [])
    .filter((event) => event && typeof event === 'object' && Object.values(SHRED_FEED_TYPES).includes(event.type))
    .map((event) => ({
      day: Math.round(toNumber(event.day)),
      type: event.type,
      id: event.id === undefined || event.id === null ? '' : String(event.id),
      bracket: event.bracket === SHRED_BRACKETS.ROOKIE ? SHRED_BRACKETS.ROOKIE : SHRED_BRACKETS.REGULAR,
      username: toText(event.username),
      rank: toPositiveRank(event.rank),
      delta: toNullableNumber(event.delta),
      improvementPct: toNullableNumber(event.improvementPct),
      sessions: toNullableNumber(event.sessions),
      totalWork: toNullableNumber(event.totalWork),
    }));

export const normalizeMe = (rawMe) => {
  if (!rawMe || typeof rawMe !== 'object') {
    return null;
  }
  return {
    id: rawMe.id === undefined || rawMe.id === null ? '' : String(rawMe.id),
    username: toText(rawMe.username),
    eligible: rawMe.eligible === true,
    reason: toText(rawMe.reason),
    entry: normalizeEntry(rawMe.entry),
  };
};

// Regular qualified, regular in progress, rookie qualified, rookie in progress.
const sortGroup = (entry) =>
  (entry.bracket === SHRED_BRACKETS.ROOKIE ? 2 : 0) + (entry.rank !== null ? 0 : 1);

const compareEntries = (a, b) => {
  const groupDiff = sortGroup(a) - sortGroup(b);
  if (groupDiff !== 0) return groupDiff;
  if (a.rank !== null && b.rank !== null) return a.rank - b.rank;
  return b.sessions - a.sessions || b.improvementPct - a.improvementPct;
};

export const normalizeResponse = (data) => {
  const source = data && typeof data === 'object' ? data : {};
  return {
    challenge: normalizeChallenge(source.challenge),
    generatedAtMs: toNullableNumber(source.generatedAtMs),
    entries: (Array.isArray(source.entries) ? source.entries : [])
      .map(normalizeEntry)
      .filter(Boolean)
      .sort(compareEntries),
    awards: normalizeAwards(source.awards),
    crew: normalizeCrew(source.crew),
    feed: normalizeFeed(source.feed),
    me: normalizeMe(source.me),
  };
};

/* ---------- ranks, qualification ---------- */

export const getRankDelta = (entry) => {
  if (!entry || entry.rank === null) {
    return { direction: 'none', amount: 0 };
  }
  if (entry.previousRank === null) {
    return { direction: 'new', amount: 0 };
  }
  const amount = entry.previousRank - entry.rank;
  if (amount > 0) return { direction: 'up', amount };
  if (amount < 0) return { direction: 'down', amount: Math.abs(amount) };
  return { direction: 'same', amount: 0 };
};

export const getSessionsToQualify = (entry, minSessions = SHRED_MIN_SESSIONS) =>
  Math.max(0, minSessions - (entry ? entry.sessions : 0));

export const getQualificationProgress = (entry, minSessions = SHRED_MIN_SESSIONS) => {
  const done = Math.min(entry ? entry.sessions : 0, minSessions);
  return { done, total: minSessions, ratio: minSessions > 0 ? done / minSessions : 0 };
};

export const getRanked = (entries) => (Array.isArray(entries) ? entries : []).filter((e) => e.qualified);

export const getChasing = (entries) => (Array.isArray(entries) ? entries : []).filter((e) => !e.qualified);

const SEED_MIX = 0x6d2b79f5;
const UINT32_RANGE = 4294967296;

const createSeededRandom = (seed) => {
  let state = seed >>> 0;
  return () => {
    state = (state + SEED_MIX) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / UINT32_RANGE;
  };
};

export const shuffleWithSeed = (items, seed) => {
  const result = Array.isArray(items) ? [...items] : [];
  const random = createSeededRandom(toNumber(seed));
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

export const isRookie = (entry) => !!entry && entry.bracket === SHRED_BRACKETS.ROOKIE;

// The podium and the fairness story are regular-bracket only; rookies have their own star.
export const getRegularRanked = (entries) => getRanked(entries).filter((e) => !isRookie(e));

export const getPodiumEntries = (entries) => getRegularRanked(entries).slice(0, 3);

export const getTopRookie = (entries) => getRanked(entries).find(isRookie) || null;

// "Level is not a head start": how many podium members sit below the median level of the field.
export const getFairnessSummary = (entries) => {
  const ranked = getRegularRanked(entries);
  const podium = ranked.slice(0, 3);
  if (podium.length === 0 || ranked.length < 3) {
    return null;
  }
  const levels = ranked.map((e) => e.level).sort((a, b) => a - b);
  const median = levels[Math.floor(levels.length / 2)];
  const lowest = podium.reduce((min, e) => (e.level < min.level ? e : min), podium[0]);
  return {
    podiumLevels: podium.map((e) => e.level),
    medianLevel: median,
    belowMedianCount: podium.filter((e) => e.level < median).length,
    lowest,
  };
};

export const isMyEntry = (entry, me, userId) => {
  if (!entry) return false;
  if (me && me.id && String(me.id) === String(entry.id)) return true;
  return !!userId && String(userId) === String(entry.id);
};

export const filterEntries = (entries, { query = '', onlyMe = false, filter = SHRED_BOARD_FILTERS.ALL, isMine }) => {
  const needle = query.trim().toLowerCase();
  return (Array.isArray(entries) ? entries : []).filter((entry) => {
    if (onlyMe) {
      return typeof isMine === 'function' ? isMine(entry) : false;
    }
    if (filter === SHRED_BOARD_FILTERS.ROOKIES && !isRookie(entry)) return false;
    if (filter === SHRED_BOARD_FILTERS.QUALIFIED && !entry.qualified) return false;
    if (filter === SHRED_BOARD_FILTERS.CHASING && entry.qualified) return false;
    if (needle && !entry.username.toLowerCase().includes(needle)) return false;
    return true;
  });
};

/* ---------- crew ---------- */

export const getCrewProgress = (crew) => {
  if (!crew || !(crew.goalWork > 0)) {
    return { pct: 0, displayPct: 0, reached: [] };
  }
  const pct = (crew.totalWork / crew.goalWork) * 100;
  return {
    pct: Math.min(100, Math.max(0, pct)),
    displayPct: Math.max(0, Math.floor(pct)),
    reached: SHRED_CREW_MILESTONES.filter((milestone) => pct >= milestone),
  };
};

/* ---------- feed ---------- */

export const buildFeedSentence = (event) => {
  if (!event) return null;
  const who = event.username || 'Someone';
  switch (event.type) {
    case SHRED_FEED_TYPES.NEW_LEADER:
      return event.improvementPct === null
        ? `${who} took the lead.`
        : `${who} took the lead at ${formatPct(event.improvementPct)}.`;
    case SHRED_FEED_TYPES.QUALIFIED:
      if (event.bracket === SHRED_BRACKETS.ROOKIE) {
        return event.rank ? `${who} joined the rookie board at #${event.rank}.` : `${who} joined the rookie board.`;
      }
      return event.sessions === null
        ? `${who} qualified for the ranked board.`
        : `${who} qualified with ${pluralize(Math.round(event.sessions), 'session')}.`;
    case SHRED_FEED_TYPES.CLIMBER: {
      const spots = event.delta === null ? 0 : Math.abs(Math.round(event.delta));
      const rankText = event.rank ? ` to #${event.rank}` : '';
      return spots > 0
        ? `${who} climbed ${pluralize(spots, 'spot')}${rankText}.`
        : `${who} moved up${rankText}.`;
    }
    case SHRED_FEED_TYPES.DAY_RECAP:
      if (event.sessions === 0 || event.totalWork === 0) return `Day ${event.day} was quiet. Nobody logged a session.`;
      if (event.totalWork === null) return `Day ${event.day} is in the books.`;
      return event.sessions === null
        ? `The crew put up ${formatCompact(event.totalWork)} work.`
        : `The crew put up ${formatCompact(event.totalWork)} work across ${pluralize(
            Math.round(event.sessions),
            'session'
          )}.`;
    default:
      return null;
  }
};

export const groupFeedByDay = (feed) => {
  const groups = [];
  (Array.isArray(feed) ? feed : []).forEach((event) => {
    const last = groups[groups.length - 1];
    if (last && last.day === event.day) {
      last.events.push(event);
    } else {
      groups.push({ day: event.day, events: [event] });
    }
  });
  return groups;
};

/* ---------- copy ---------- */

const formatTimeZoneLabel = (timezone) => {
  const city = typeof timezone === 'string' ? timezone.split('/').pop().replace(/_/g, ' ').trim() : '';
  return city ? `${city} time` : 'the gym\'s local time';
};

export const buildRules = (challenge) => {
  const c = challenge || normalizeChallenge(null);
  const range = formatDateRange(c.startMs, c.endMs, c.timezone);
  const rules = [
    `${c.dayCount} days${range ? ` (${range})` : ''}. Days run midnight to midnight, ${formatTimeZoneLabel(c.timezone)}.`,
    'Everyone with a Black Box account competes automatically. There is nothing to sign up for.',
    'Your score is the percent change between your average work per session during the contest and your own baseline, so level and strength give no head start.',
    `Your baseline is your own average work per session over the ${c.baselineDays} days before the contest (${SHRED_BASELINE_WINDOW_LABEL}), and it does not change during the contest. Sessions on ${SHRED_BASELINE_GAP_LABEL} fall between the baseline and the contest and count toward neither.`,
    `Regular, Boss Battle and Multiplayer sessions count, but only sessions of ${SHRED_MAX_SESSION_MINUTES} minutes or less. A session that runs longer, or is extended past ${SHRED_MAX_SESSION_MINUTES} minutes, does not count at all.`,
    `Short sessions count during the contest, but your baseline and the Iron Habit award use full-length sessions: ${SHRED_FULL_SESSION_MINUTES}+ minutes with at least ${SHRED_ACTIVE_PLAY_MINUTES} minutes of active play.`,
    `Active play is measured from your sets. Playing for a few minutes and letting the timer run out does not make a full-length session, and a short break between sets of up to 5 minutes is fine.`,
    'Demo and test builds, and sessions with no recorded work, do not count. Sessions show up on the board once they have synced after your workout.',
    `To appear on the ranked board you need ${c.minSessions}+ sessions during the contest. Until then you are still chasing, and your row shows how close you are.`,
    'A session counts on the day it starts. If two members tie on score, the one with more sessions ranks higher.',
    'Six awards: Shred Champion, Underdog, Pace King, Iron Engine and Iron Habit, plus Rookie Star for the Rookie bracket. Only ranked members can win an award.',
  ];
  if (c.rewards.participation) rules.push(c.rewards.participation);
  if (c.rewards.podium) rules.push(c.rewards.podium);
  if (c.rewards.crewGoal) rules.push(c.rewards.crewGoal);
  rules.push('Winners are confirmed after the contest ends and rewards are given out by the Black Box team shortly afterwards.');
  return rules;
};

export const buildRookieRules = (challenge) => {
  const c = challenge || normalizeChallenge(null);
  return [
    `You are a Rookie if you have fewer than ${c.minBaselineSessions} full-length sessions (${SHRED_FULL_SESSION_MINUTES} to ${SHRED_MAX_SESSION_MINUTES} minutes, with at least ${SHRED_ACTIVE_PLAY_MINUTES} minutes of active play) in the baseline window. That includes brand-new members, members coming back after a break, and anyone who mostly plays short sessions or leaves the timer running.`,
    'Your bracket is set from your baseline when the contest starts. It does not change during the contest, so doing more sessions does not move you out of the Rookie bracket.',
    `Your first ${c.rookieBaselineSessions} contest sessions set your baseline. Every session after that is scored against it, so your score starts at 0% and shows once you have a session ${c.rookieBaselineSessions + 1}.`,
    `You still need ${c.minSessions} sessions in total to rank. The sessions that set your baseline count toward that total.`,
    'Rookies rank against other rookies, on their own board.',
    'Rookies compete for the Rookie Star award, and earn the participation reward like everyone else. The other five awards are for the regular bracket.',
  ];
};

export const resolveAvatarSrc = (id) =>
  id ? `${SHRED_FALLBACK_AVATAR_BASE}/${encodeURIComponent(id)}.png` : '';

export const getDemoMode = (search) => {
  try {
    const params = new URLSearchParams(search || '');
    const value = params.get('demo');
    return value ? value : null;
  } catch (e) {
    return null;
  }
};

/* ---------- ghost, nudge, notices, share ---------- */

// 0% is the member's "ghost" (their baseline self). The marker position is clamped to +-range.
export const getGhostPosition = (pct, range = SHRED_GHOST_RANGE_PCT) => {
  const value = toNumber(pct);
  const clamped = Math.max(-range, Math.min(range, value));
  return {
    pct: value,
    clamped,
    ratio: (clamped / range + 1) / 2,
    ahead: value > 0 ? true : value < 0 ? false : null,
  };
};

export const buildGhostCopy = (pct) => {
  const value = toNumber(pct);
  if (value > 0) return `You are ${formatPct(value).replace('+', '')} ahead of your ghost.`;
  if (value < 0) return `Your ghost is ${formatPct(Math.abs(value)).replace('+', '')} ahead. Catch it.`;
  return 'Neck and neck with your ghost.';
};

const MS_PER_DAY_NUDGE = 24 * 60 * 60 * 1000;

export const getQualificationNudge = ({ entry, challenge, nowMs = Date.now() }) => {
  if (!entry || !challenge || entry.qualified || !Number.isFinite(challenge.endMs)) {
    return null;
  }
  const need = getSessionsToQualify(entry, challenge.minSessions);
  if (need === 0) return null;
  const msLeft = challenge.endMs - nowMs;
  if (msLeft <= 0) return null;
  const daysLeft = Math.max(1, Math.ceil(msLeft / MS_PER_DAY_NUDGE));
  return { need, daysLeft, reachable: need <= daysLeft * SHRED_MAX_SESSIONS_PER_DAY };
};

export const buildNudgeText = (nudge) => {
  if (!nudge) return '';
  if (!nudge.reachable) {
    return `Ranking is out of reach this time, but every session still counts toward your own baseline.`;
  }
  return `${pluralize(nudge.need, 'more session')} in ${pluralize(nudge.daysLeft, 'day')} to rank.`;
};

// Compares the signed-in member's entry across two live polls of the same bracket.
export const getRankChangeNotice = (previous, next) => {
  if (!previous || !next || next.rank === null) return null;
  if (previous.rank === null) return `You are on the board at #${next.rank}`;
  if (previous.bracket !== next.bracket || previous.rank === next.rank) return null;
  return next.rank < previous.rank ? `You moved up to #${next.rank}` : `You dropped to #${next.rank}`;
};

export const buildShareText = (baseText, entry, pctText = formatPct) =>
  entry && entry.qualified && entry.rank !== null
    ? `${baseText} I am #${entry.rank} at ${pctText(entry.improvementPct)} against my own baseline.`
    : baseText;

export const getPrizeList = (awards, rewards) => {
  const awardPrizes = (Array.isArray(awards) ? awards : [])
    .map((award) => {
      const items = Array.isArray(award?.reward?.items) ? award.reward.items.filter((item) => typeof item === 'string') : [];
      return {
        id: award?.id,
        icon: award?.icon,
        accent: award?.accent,
        title: award?.title,
        text: items.length > 0 ? items.join(' + ') : toText(award?.reward?.title),
        items: items.length > 0 ? items : [toText(award?.reward?.title)].filter(Boolean),
      };
    })
    .filter((prize) => prize.id && prize.text);
  const source = rewards && typeof rewards === 'object' ? rewards : {};
  const extras = [
    { id: SHRED_PRIZE_IDS.CREW, icon: '🎁', accent: SHRED_COLORS.gold, title: SHRED_COPY.PRIZE_CREW_TITLE, text: toText(source.crewGoal), items: [toText(source.crewGoal)] },
    { id: SHRED_PRIZE_IDS.PODIUM, icon: '🥇', accent: SHRED_COLORS.silver, title: SHRED_COPY.PRIZE_PODIUM_TITLE, text: toText(source.podium), items: [toText(source.podium)] },
  ].filter((prize) => prize.text);
  return [...awardPrizes, ...extras];
};

export const getNextPrizeDropDelayMs = (random, isFirst = false) =>
  (isFirst ? SHRED_TV_PRIZE_DROP_FIRST_MIN_MS : SHRED_TV_PRIZE_DROP_GAP_MIN_MS) +
  Math.floor(random * SHRED_TV_PRIZE_DROP_GAP_SPREAD_MS);

export const pickRandomPrize = (prizes, random) =>
  Array.isArray(prizes) && prizes.length > 0 ? prizes[Math.min(Math.floor(random * prizes.length), prizes.length - 1)] : null;
